/**
 * One live session's report at its own address (`/rooms/:id/sessions/:sessionId`), drawn as a room's Last live tab
 * draws its last one. It loads itself from the address, because a host can land here straight from the studio.
 */
import { useNavigate, useParams } from "react-router";
import { useLayout } from "../../components/season/layout";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Icon } from "../../components/ui/Icon";
import "../../styles/rooms.css";
import { LastLiveTab } from "./LastLiveTab";
import { longDayTime } from "./liveWords";
import { SeasonSession } from "./SeasonSession";
import { useSessionPage } from "./useSessionPage";

/** A session's report: the computer's page on a computer, the Season page on a phone and an iPad. */
export function SessionReportView() {
  const layout = useLayout();
  return layout === "computer" ? <ComputerSession /> : <SeasonSession layout={layout} />;
}

function ComputerSession() {
  const { id = "", sessionId = "" } = useParams<{ id: string; sessionId: string }>();
  const navigate = useNavigate();
  const { detail, live, phase } = useSessionPage(id, sessionId);

  const back = () => navigate(`/rooms/${id}/sessions`);
  return (
    <div className="page rooms">
      <button type="button" className="back" onClick={back}>
        <Icon name="left" /> {detail?.title ?? "Sessions"}
      </button>
      {phase === "loading" ? (
        <div className="card sk" style={{ height: 240 }} aria-busy="true" />
      ) : phase === "error" || !detail || !live ? (
        <EmptyState icon="info" title="We couldn't load this session" actions={<Button onClick={back}>Back to the sessions</Button>} />
      ) : live.state !== "ended" ? (
        // The address exists whether or not the session has run, so a bookmark for a week to come says so.
        <EmptyState
          icon="clock"
          title="Nothing to report yet"
          text={live.state === "live" ? "It's on now. The report is here once you end it." : `It runs ${longDayTime(live.startsAt, live.timezone)}.`}
          actions={<Button onClick={back}>Back to the sessions</Button>}
        />
      ) : (
        <>
          <div className="ph">
            <div>
              <h1 dir="auto">{detail.title}</h1>
              <p>Session {live.seq}</p>
            </div>
          </div>
          <LastLiveTab webinarId={detail.id} title={detail.title} live={live} seriesSession={!!detail.live?.series} />
        </>
      )}
    </div>
  );
}
